import { useSelector } from "react-redux";

import { useGetSettings } from "@/api/configuration/useConfigurationAPI";
import { countTotalRows } from "@/components/spatialAnalysis/catchmentDataSection/catchmentDataset";
import { MAX_ISOCHRONE_CATCHMENTS } from "@/constants/dataset";
import { RootState } from "@/store";
import { CatchmentSettings, ECatchmentInputBy } from "@/types/spatial";

/**
 * Counts the catchment inputs the user configured: pinned markers on the
 * Location tab, dataset rows on the Dataset tab. The Polygon tab defines its own
 * areas and never reaches the catchment-type section, so it counts as zero.
 *
 * This is the count the user sees, so it is deliberately not deduplicated —
 * `useGetBulkIsochroneQuery` asks the routing service once per *distinct*
 * coordinate, making this an upper bound on the routing jobs actually issued.
 */
const countIsochroneCatchments = (
  settings: Pick<
    CatchmentSettings,
    "catchmentInputBy" | "datasetConfig" | "placeCoordinates"
  >
): number => {
  switch (settings.catchmentInputBy) {
    case ECatchmentInputBy.BY_DATASET:
      return countTotalRows(settings.datasetConfig?.datasets);
    case ECatchmentInputBy.BY_LOCATION:
      return settings.placeCoordinates?.length ?? 0;
    default:
      return 0;
  }
};

const useIsochroneCatchmentLimit = () => {
  const { catchmentSettingsTemp } = useSelector(
    (state: RootState) => state.spatial
  );
  const { data, isPending } = useGetSettings();

  const limit = data?.max_isochrone_catchments || MAX_ISOCHRONE_CATCHMENTS;
  const totalCatchments = countIsochroneCatchments(catchmentSettingsTemp);

  return {
    limit,
    totalCatchments,
    isLimitReady: !isPending,
    isRoadAccessOverLimit: !isPending && totalCatchments > limit
  };
};

export default useIsochroneCatchmentLimit;
